const snakeCase = require('lodash/snakeCase')
const EntityApi = require('./entity-api')
const { singularize } = require('../utils/pluralize')

const supportedActionNames = [
  'list',
  'get',
  'create',
  'update',
  'delete'
]

const REST_ACTION_TO_HTTP_METHOD = {
  list: 'get',
  get: 'get',
  create: 'post',
  update: 'put',
  delete: 'delete'
}

/**
 * Proxified Entity API for dynamically generating method proxies, so an API call will trigger an HTTP request to agave
 * instead of e.g. trying to directly connect to PostgreSQL if there's no PostgreSQL connection.
 * Extends the EntityApi and allows for the dynamic creation of proxy API methods based on provided method names.
 * @class
 * @extends EntityApi
 */
class ProxifiedEntityApi extends EntityApi {
  constructor (entityName, methodsNeedingProxy, needsProxy, adapter, agaveAdapter) {
    super(adapter)
    this.agaveAdapter = agaveAdapter
    if (needsProxy) {
      this.proxifyMethods(entityName, methodsNeedingProxy)
    } else {
      // Return the routes so our HTTP REST API can automatically link routes to api methods
      this.proxyAutoRoutes = this.getURLPaths(entityName, methodsNeedingProxy)
    }
  }

  getProxyConfig (entityName, methodName, restAction) {
    // Split snake case method name into action and sub-entity parts
    const [actionNameCandidate, ...subEntityCandidateParts] = snakeCase(methodName).split('_')

    if (!supportedActionNames.includes(actionNameCandidate)) {
      // If the action is not supported, the method name is the sub-entity name
      // and the action is a GET if not provided a custom restAction
      return {
        restAdapterAction: restAction || 'GET',
        pathToEndpoint: `${entityName}/${snakeCase(methodName).split('_').join('-')}`
      }
    }

    // If the action is supported, we take that as the restAdapterAction
    // and we use the rest of the name as the sub-entity name
    // if the action is list, we convert the sub-entity name to singular form (e.g. listLocations should be a GET on /location/)
    // We target this form: "http://dev.psm.localhost:3000/agave/v1/shipment/last-mile-delivery/?limit=500"
    const subEntityOrMethodName = actionNameCandidate === 'list'
      ? singularize(subEntityCandidateParts.join('-')) || ''
      : subEntityCandidateParts.join('-')

    return {
      restAdapterAction: actionNameCandidate || restAction,
      pathToEndpoint: `${entityName}/${subEntityOrMethodName}`
    }
  }

  proxify (func, entityName, methodName, restAction, cache = true) {
    const structureRequestParameters = (params, action) => {
      if (action === 'list') {
        const [queryParams] = params
        if (!cache) {
          queryParams['_'] = new Date().toJSON() // Add cache-busting param to avoid caching
        }
        return { requestParams: queryParams }
      }
      if (action === 'get') {
        const [id, queryParams] = params
        if (!cache) {
          queryParams['_'] = new Date().toJSON() // Add cache-busting param to avoid caching
        }
        return { pathExtension: id, requestParams: queryParams }
      }
      if (action === 'update') {
        const [id, body] = params
        return { pathExtension: id, requestParams: body }
      }
      if (action === 'create') {
        const [body] = params
        return { requestParams: body }
      }
      return params
    }
    this[methodName] = async (...params) => {
      const {restAdapterAction, pathToEndpoint} = this.getProxyConfig(entityName, methodName, restAction)
      const { pathExtension = '', requestParams } = structureRequestParameters(params, restAdapterAction)
      return this.agaveAdapter[restAdapterAction](`${pathToEndpoint}${pathExtension}`, requestParams)
    }
  }

  proxifyMethods (entityName, methodsNeedingProxy) {
    methodsNeedingProxy.forEach(method => {
      const {methodName, restAction, cache} = typeof method === 'object'
        ? method
        : {methodName: method}
      this.proxify(this[methodName], entityName, methodName, restAction, cache)
    })
  }

  getURLPaths (entityName, methodsNeedingProxy) {
    const paths = methodsNeedingProxy.map(methodName => {
      const config = this.getProxyConfig(entityName, methodName)
      return config && config.pathToEndpoint
    })
    return paths
  }

  /**
   * Returns route configs for methods marked with `autoRegister: true`.
   * Intended for server-side use (e.g. agave) to auto-register generic proxy handlers
   * without writing a custom controller for each method.
   *
   * Path derivation: the camelCase method name is converted to snake_case and split on '_'.
   * - If the first part is a known REST action (list/get/create/update/delete), it becomes
   *   the HTTP verb and the remaining parts form the sub-path.
   *   e.g. 'createStop' → POST /stop
   * - Otherwise the entire name is the sub-path and `restAction` (defaulting to 'create') is the verb.
   *   e.g. 'moveStop' with restAction:'create' → POST /move-stop
   *
   * @param {Array} methodsNeedingProxy - the methodsNeedingProxy array from the API class
   * @returns {Array<{ path: string, httpMethod: string, methodName: string }>}
   */
  static getAutoRegisterConfigs (methodsNeedingProxy) {
    return methodsNeedingProxy
      .filter(m => typeof m === 'object' && m.autoRegister)
      .map(({ methodName, restAction, permissions }) => {
        const snakeParts = snakeCase(methodName).split('_')
        const actionCandidate = snakeParts[0]
        const effectiveAction = supportedActionNames.includes(actionCandidate)
          ? actionCandidate
          : restAction || 'create'
        const subPath = supportedActionNames.includes(actionCandidate)
          ? snakeParts.slice(1).join('-')
          : snakeParts.join('-')
        const pathToEndpoint = `/${subPath || ''}`
        const httpMethod = REST_ACTION_TO_HTTP_METHOD[effectiveAction] || 'post'
        return { path: pathToEndpoint, httpMethod, methodName, permissions }
      })
  }
}

module.exports = ProxifiedEntityApi
